import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { Database, FileText, RefreshCw, BookOpen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { itemVariants } from "./motion";
import type { KnowledgeBaseStatus } from "../mockAutomations";

interface KnowledgeBaseCardProps {
  status: KnowledgeBaseStatus;
  onResync: () => void;
}

export function KnowledgeBaseCard({ status, onResync }: KnowledgeBaseCardProps) {
  const [syncing, setSyncing] = useState(false);

  const handleResync = () => {
    if (syncing) return;
    setSyncing(true);
    window.setTimeout(() => {
      setSyncing(false);
      onResync();
      toast.success("Knowledge base re-synced");
    }, 1200);
  };

  return (
    <motion.div variants={itemVariants}>
      <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
              <BookOpen className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              Knowledge Base Status
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Synced Grounding
            </span>
          </div>
          <Button
            size="sm"
            onClick={handleResync}
            disabled={syncing}
            className="h-7.5 px-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold shadow-xs gap-1.5 transition-colors"
          >
            <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Re-sync Index"}
          </Button>
        </div>

        <div className="p-3 bg-white/70 dark:bg-slate-900/70">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <div className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                <Database className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                  {status.productsSynced.toLocaleString()}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Products Synced</div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                <FileText className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
                  {status.faqDocs}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">FAQ Documents</div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <RefreshCw className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold tracking-tight text-slate-900 dark:text-white truncate">
                  {status.lastSyncedAt}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Synced</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
